import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { Navigate, useLocation } from 'react-router-dom';
import { effectiveRole, useAuthStore } from '@/store/auth';
/**
 * Coarse-tier route guard — Phase-1 RBAC layered on top of `<RoleGate>`.
 *
 * Use for routes whose access policy is naturally tier-based:
 *   <RoleGuard allowedRoles={['ADMIN']}>           ← Settings, User Management
 *   <RoleGuard allowedRoles={['ADMIN', 'MANAGER']}> ← Reports & Analytics
 *   <RoleGuard allowedRoles={['ADMIN', 'MANAGER', 'AGENT']}> ← Tickets dashboard
 *
 * Behaviour:
 *  - Not logged in → redirect to /login, preserving `from` for post-login bounce.
 *  - Logged in but role not in `allowedRoles` → redirect to /access-denied
 *    (or the explicit `fallback` route).
 *  - Allowed → render children.
 */
export function RoleGuard({ allowedRoles, children, fallback = '/access-denied' }) {
    const me = useAuthStore((s) => s.me);
    const location = useLocation();
    if (!me) {
        return _jsx(Navigate, { to: "/login", replace: true, state: { from: location.pathname } });
    }
    const role = effectiveRole(me);
    if (role === null || !allowedRoles.includes(role)) {
        return _jsx(Navigate, { to: fallback, replace: true, state: { from: location.pathname, requiredRoles: allowedRoles } });
    }
    return _jsx(_Fragment, { children: children });
}
